import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm, useFieldArray, FormProvider, DefaultValues } from 'react-hook-form';

import { ST, LocationCreateDTO, LocationCreateSchema, Location } from '_lib/api/apiTypes.shim';

import { Box } from '_lib/components/Box/Box';
import { Form } from '_lib/components/Form/Form';
import { Grid } from '_lib/components/Grid/Grid';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { BaseAddressForm } from '../FormItems/BaseAddressForm';
import { ContactListFormItem } from '../FormItems/ContactListFormItem';

import type { ApiSubmitHandler } from '_lib/types/hookForm.types';

export interface LocationFormProps {
  isDisabled: boolean;
  providerCustomerId: string;
  formId?: string;
  children?: React.ReactElement;
  location?: Location;
  onLocationSubmit: ApiSubmitHandler<LocationCreateDTO>;
}

const defaultLocationFormValues = (
  providerCustomerId: string,
  location: Location | undefined
): DefaultValues<LocationCreateDTO> =>
  location
    ? {
        providerCustomerId: location.providerCustomerId,
        name: location.name,
        addressLine1: location.addressLine1,
        addressLine2: location.addressLine2,
        city: location.city,
        stateOrProvince: location.stateOrProvince,
        zipOrPostal: location.zipOrPostal,
        country: location.country,
        contacts: undefined // we are never going to use this form to edit contacts, just add them
      }
    : {
        providerCustomerId,
        contacts: [
          { type: ST.ContactTypeEnum.MobilePhone, value: '' },
          { type: ST.ContactTypeEnum.Email, value: '' }
        ]
      };

export const LocationForm = (props: LocationFormProps): React.ReactElement => {
  const { location, formId, onLocationSubmit, providerCustomerId, isDisabled, children } = props;

  const formContext = useForm<LocationCreateDTO>({
    resolver: zodResolver(LocationCreateSchema),
    defaultValues: defaultLocationFormValues(providerCustomerId, location),
    mode: 'onChange',
    shouldFocusError: true
  });

  const { handleSubmit, setError, trigger, control } = formContext;

  const {
    fields: contactFields,
    append: contactAppend,
    remove: contactRemove
  } = useFieldArray<LocationCreateDTO>({
    name: 'contacts',
    control
  });

  const [previousLocationId, setPreviousLocationId] = useState<string | undefined>();
  if (location?.providerId && location.providerId !== previousLocationId) {
    setPreviousLocationId(location.providerId);
    trigger(undefined, { shouldFocus: true });
  }

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <FormProvider {...formContext}>
        <Form
          id={formId || 'st-api-call-pop-location-form'}
          onSubmit={apiSubmitHandler(handleSubmit, setError, onLocationSubmit)}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12}>
              <TextFieldFormItem disabled={isDisabled} name={'name'} label='Name' control={control} required />
            </Grid>
            <BaseAddressForm
              formContext={formContext}
              disabled={isDisabled}
              isEditingExisting={!!location}
              isNested={false}
            />
            <Grid item xs={12} sm={12}>
              {!location && (
                <>
                  <ContactListFormItem
                    contactFields={contactFields}
                    contactsPath={'contacts'}
                    contactAppend={(contact) => contactAppend(contact)}
                    contactRemove={(index) => contactRemove(index)}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </Form>
      </FormProvider>
      <Grid container spacing={1} sx={{ marginTop: 1 }}>
        {children && (
          <Grid item xs={12} sm={12}>
            {children}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
